import { useState, useEffect, useRef, Fragment } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { ShoppingBag, Plus, Trash2, PackageCheck, CheckCircle2, XCircle, Pencil, FileText, Truck, GanttChartSquare, PackageX, ShoppingCart, AlertTriangle, Factory, Sparkles, Lock, Wrench } from "lucide-react";
import { useTn } from "@/lib/i18n";
import { fmtQty } from "@/lib/constants";
import { DT } from "@/components/ui/denseTable";
import { ListPagination, usePager } from "@/components/ui/ListPagination";
import { ImportExportButtons } from "@/components/ImportExportButtons";
import AiPoImportDialog from "./AiPoImportDialog";
import { API, api } from "@/lib/api";
import { formatINR } from "@/lib/money";
import { SHOW_COSTING } from "@/lib/features";

const STATUS_STYLE = {
  Pending: "bg-amber-100 text-amber-700",
  Confirmed: "bg-blue-100 text-blue-700",
  Fulfilled: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-slate-200 text-slate-500",
};

// List ordering: confirmed (unfulfilled) first, then pending, then cancelled & fulfilled.
const SO_ORDER = { Confirmed: 0, Pending: 1, Cancelled: 2, Fulfilled: 3 };

function fgLabel(i) {
  return `${i.name} (${i.sku}) · ${fmtQty(i.stock_quantity, i.unit)} ${i.unit} in stock`;
}

function SalesOrderDialog({ customers, finishedGoods, existing, onClose, onSave }) {
  const isEdit = !!existing;
  const nextKey = useRef(0);
  const mk = (obj) => ({ _key: `ln-${nextKey.current++}`, ...obj });
  const [customerId, setCustomerId] = useState(existing?.customer_id || "");
  const [lines, setLines] = useState(
    existing?.items?.length ? existing.items.map((it) => mk({ item_id: it.item_id, quantity: String(it.quantity), price: it.unit_price ? String(it.unit_price) : "" })) : [mk({ item_id: "", quantity: "", price: "" })]
  );
  const [poNumber, setPoNumber] = useState(existing?.customer_po_number || "");
  const [poDate, setPoDate] = useState(existing?.po_date || "");
  const [delivery, setDelivery] = useState(existing?.required_delivery_date || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [saving, setSaving] = useState(false);

  const setLine = (idx, key, val) => setLines((p) => p.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  const addLine = () => setLines((p) => [...p, mk({ item_id: "", quantity: "", price: "" })]);
  const removeLine = (idx) => setLines((p) => p.filter((_, i) => i !== idx));

  const valid = customerId && lines.some((l) => l.item_id && Number(l.quantity) > 0);
  const grandTotal = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.price) || 0), 0);

  const save = async () => {
    const items = lines
      .filter((l) => l.item_id && Number(l.quantity) > 0)
      .map((l) => {
        const fg = finishedGoods.find((f) => f.id === l.item_id);
        return { item_id: fg.id, sku: fg.sku, name: fg.name, unit: fg.unit || "pcs", quantity: Number(l.quantity), unit_price: Number(l.price) || 0 };
      });
    setSaving(true);
    try {
      const payload = {
        items,
        customer_po_number: poNumber || null,
        po_date: poDate || null,
        required_delivery_date: delivery || null,
        notes: notes || null,
      };
      if (!isEdit) payload.customer_id = customerId;  // customer is fixed once created
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="sales-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? `Edit ${existing.so_number}` : "New Sales Order"}</DialogTitle>
          <DialogDescription className="text-xs">{isEdit ? "Update this sales order. Stock is only affected on fulfilment." : "Record a customer order for finished goods. Stock ships on fulfilment."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">Customer *</Label>
            <SearchableSelect
              value={customerId}
              onValueChange={setCustomerId}
              disabled={isEdit}
              placeholder="Select a customer"
              searchPlaceholder="Search customers…"
              emptyText="No customers found."
              options={customers.filter((c) => c.active !== false).map((c) => ({ value: c.id, label: `${c.name} · ${c.code}`, keywords: `${c.name} ${c.code}` }))}
              testid="so-customer-select"
            />
          </div>
          <div className="space-y-1"><Label className="text-xs">Customer PO Number</Label><Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} data-testid="so-po-number" /></div>
          <div className="space-y-1"><Label className="text-xs">PO Date</Label><Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} data-testid="so-po-date" /></div>
          <div className="space-y-1 col-span-2"><Label className="text-xs">Required Delivery Date</Label><Input type="date" value={delivery} onChange={(e) => setDelivery(e.target.value)} data-testid="so-delivery-date" /></div>
        </div>

        <div className="mt-2 space-y-2">
          <Label className="text-xs">Finished Goods *</Label>
          {lines.map((l, idx) => {
            const lineTotal = (Number(l.quantity) || 0) * (Number(l.price) || 0);
            return (
            <div key={l._key} className="flex items-end gap-2" data-testid={`so-line-${idx}`}>
              <div className="flex-1 space-y-1">
                <SearchableSelect
                  value={l.item_id}
                  onValueChange={(v) => setLine(idx, "item_id", v)}
                  placeholder="Select finished good"
                  searchPlaceholder="Search products…"
                  emptyText="No products found."
                  options={finishedGoods.map((f) => ({ value: f.id, label: fgLabel(f), keywords: `${f.sku} ${f.name}` }))}
                  testid={`so-item-select-${idx}`}
                />
              </div>
              <div className="w-20 space-y-1">
                <Input type="number" min="1" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} data-testid={`so-qty-${idx}`} />
              </div>
              {SHOW_COSTING && (
                <>
                  <div className="w-28 space-y-1">
                    <Input type="number" min="0" step="0.01" placeholder="Unit ₹" value={l.price} onChange={(e) => setLine(idx, "price", e.target.value)} data-testid={`so-price-${idx}`} />
                  </div>
                  <div className="w-24 text-right text-[11px] text-slate-600 font-mono-data pb-2 shrink-0" data-testid={`so-line-total-${idx}`}>{lineTotal > 0 ? formatINR(lineTotal) : "—"}</div>
                </>
              )}
              {lines.length > 1 && (
                <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500" onClick={() => removeLine(idx)} data-testid={`so-line-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
            );
          })}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={addLine} data-testid="so-add-line"><Plus className="h-3 w-3 mr-1" /> Add Item</Button>
            {SHOW_COSTING && <div className="text-xs text-slate-600">Order Total: <span className="font-mono-data font-semibold text-slate-900" data-testid="so-grand-total">{formatINR(grandTotal)}</span></div>}
          </div>
        </div>

        <div className="space-y-1 mt-2"><Label className="text-xs">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="so-notes" /></div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={!valid || saving} onClick={save} data-testid="so-save">{saving ? "Saving…" : isEdit ? "Save Changes" : "Create Sales Order"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RaisePODialog({ material, onClose, onSubmit }) {
  const [qty, setQty] = useState(String(material.suggested_order_qty ?? Math.max(1, Math.ceil(material.shortage || 0))));
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({
        item_id: material.item_id, sku: material.sku, name: material.name, unit: material.unit,
        suggested_qty: Math.max(1, Number(qty) || 0), unit_price: Number(price) || 0,
        supplier: material.supplier, lead_time_days: material.lead_time_days,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };
  const lineTotal = (Number(qty) || 0) * (Number(price) || 0);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="so-raise-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Raise Purchase Order</DialogTitle>
          <DialogDescription className="text-xs">Order the short material to close the gap. Review the quantity before submitting.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-sm font-semibold text-slate-800">{material.name}</p>
            <p className="text-[11px] text-slate-400 font-mono-data">{material.sku}{material.supplier ? ` · ${material.supplier}` : " · no supplier on file"}</p>
            <p className="text-[11px] text-red-600 mt-1 font-mono-data">short {fmtQty(material.shortage, material.unit)} {material.unit} · in stock {fmtQty(material.in_stock, material.unit)} {material.unit}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Order Quantity ({material.unit})</Label>
              <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="so-raise-po-qty" />
            </div>
            {SHOW_COSTING && (
              <div className="space-y-1">
                <Label className="text-xs">Unit Price (₹)</Label>
                <Input type="number" min="0" step="0.01" placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="so-raise-po-price" />
              </div>
            )}
          </div>
          {SHOW_COSTING && <div className="text-right text-xs text-slate-600">Line Total: <span className="font-mono-data font-semibold text-slate-900" data-testid="so-raise-po-total">{formatINR(lineTotal)}</span></div>}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={saving || Number(qty) < 1} onClick={submit} data-testid="so-raise-po-submit">
            <ShoppingCart className="h-3.5 w-3.5 mr-1" /> {saving ? "Raising…" : "Raise PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Blue "Create Work Order" flow — build a short Manufactured Spare Part in-house, linked to the SO.
function CreateWorkOrderFromSODialog({ material, so, onClose, onSubmit }) {
  const [qty, setQty] = useState(String(material.suggested_wo_qty ?? Math.max(1, Math.ceil(material.shortage || 0))));
  const [due, setDue] = useState(so.required_delivery_date || new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({ item_id: material.item_id, quantity: Math.max(1, Number(qty) || 0), due_date: due || null });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="so-create-wo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Wrench className="h-4 w-4 text-blue-600" /> Create Work Order</DialogTitle>
          <DialogDescription className="text-xs">Build this manufactured spare part in-house for {so.so_number}. Review the quantity before it hits the factory floor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
            <p className="text-sm font-semibold text-slate-800">{material.name}</p>
            <p className="text-[11px] text-slate-400 font-mono-data">{material.sku} · Mfg. Spares</p>
            <p className="text-[11px] text-blue-700 mt-1 font-mono-data">short {fmtQty(material.shortage, material.unit)} {material.unit} · in stock {fmtQty(material.in_stock, material.unit)} {material.unit}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Build Quantity ({material.unit})</Label>
              <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="so-create-wo-qty" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Due Date</Label>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="so-create-wo-due" />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={saving || Number(qty) < 1} onClick={submit} data-testid="so-create-wo-submit">
            <Wrench className="h-3.5 w-3.5 mr-1" /> {saving ? "Creating…" : "Create Work Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Chain reaction — after a Work Order is raised, offer POs for the raw materials needed to build it.
function ChainPODialog({ view, onClose, onRaisePO }) {
  const [ordered, setOrdered] = useState({});
  if (!view) return null;
  const { wo, materials } = view;
  const raise = async (m) => {
    await onRaisePO({ item_id: m.item_id, sku: m.sku, name: m.name, unit: m.unit, suggested_qty: m.suggested_order_qty || Math.max(1, Math.ceil(m.shortage || 0)), unit_price: 0, supplier: m.supplier, lead_time_days: m.lead_time_days });
    setOrdered((p) => ({ ...p, [m.item_id]: true }));
  };
  const raiseAll = async () => { for (const m of materials) { if (!ordered[m.item_id]) await raise(m); } };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="so-chain-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Factory className="h-4 w-4 text-blue-600" /> Work Order {wo?.order_code || ""} created</DialogTitle>
          <DialogDescription className="text-xs">
            {materials.length > 0
              ? "These raw materials are needed to build this spare part in-house. Raise POs to secure them."
              : "All raw materials to build this spare part are already in stock — nothing to purchase."}
          </DialogDescription>
        </DialogHeader>
        {materials.length > 0 && (
          <>
            <div className="flex justify-end">
              <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={raiseAll} data-testid="so-chain-raise-all">
                <ShoppingCart className="h-3 w-3 mr-1" /> Raise all POs
              </Button>
            </div>
            <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden mt-1">
              {materials.map((m) => (
                <div key={m.item_id} className="flex items-center gap-2 px-2.5 py-1.5" data-testid={`so-chain-mat-${m.sku}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-slate-800 truncate">{m.name} <span className="font-mono-data text-slate-400">{m.sku}</span></p>
                    <p className="text-[10px] text-slate-400 font-mono-data">need {fmtQty(m.required, m.unit)} · in stock {fmtQty(m.in_stock, m.unit)} · <span className="text-red-600 font-semibold">short {Number(m.shortage).toLocaleString("en-IN")} {m.unit}</span>{m.supplier ? ` · ${m.supplier}` : ""}</p>
                  </div>
                  {ordered[m.item_id] ? (
                    <span className="text-[10px] font-semibold text-emerald-600 shrink-0" data-testid={`so-chain-ordered-${m.sku}`}>Ordered ✓</span>
                  ) : (
                    <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={() => raise(m)} data-testid={`so-chain-raise-${m.sku}`}>
                      <ShoppingCart className="h-3 w-3 mr-1" /> Raise PO
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} data-testid="so-chain-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Fulfil Guard — compares each finished-good line against current FG stock.
// green = every line fully covered · yellow = some stock but not all covered · grey = nothing available.
const GUARD = {
  green: { label: "Ready to Fulfil", cls: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200", icon: CheckCircle2 },
  yellow: { label: "Partial Stock", cls: "bg-amber-100 text-amber-700 hover:bg-amber-200", icon: AlertTriangle },
  grey: { label: "Awaiting Production", cls: "bg-slate-200 text-slate-500 hover:bg-slate-300", icon: Factory },
};

function guardFor(so, stockById) {
  const lines = (so.items || []).map((it) => {
    const b = stockById[it.item_id];
    const onHand = Number(b?.stock_quantity || 0);
    const qty = Number(it.quantity || 0);
    const covered = onHand >= qty;
    return { item_id: it.item_id, sku: it.sku, name: it.name, unit: it.unit, qty, onHand, covered, short: Math.max(0, qty - onHand), hasSome: onHand > 0 };
  });
  const total = lines.length;
  const ready = lines.filter((l) => l.covered).length;
  const someStock = lines.filter((l) => l.hasSome).length;
  let level = "yellow";
  if (total > 0 && ready === total) level = "green";
  else if (someStock === 0) level = "grey";
  return { level, ready, total, lines };
}

function GuardDrawer({ view, orders = [], onClose, onJumpToGantt }) {
  if (!view) return null;
  const { so, guard } = view;
  const meta = GUARD[guard.level];
  const Icon = meta.icon;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sales-guard-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Icon className="h-4 w-4" /> Fulfil Guard — {so.so_number}</DialogTitle>
          <DialogDescription className="text-xs">
            {guard.ready} of {guard.total} item(s) fully in stock. {guard.level === "green" ? "This order is ready to fulfil." : "Items highlighted in red are blocking the shipment."}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden mt-1">
          {guard.lines.map((l, gi) => {
            const linked = (orders || []).filter((o) => o.sales_order_id === so.id && o.bom_item_id === l.item_id);
            return (
              <div key={`${l.item_id}-${gi}`} className={`flex items-center gap-2 px-3 py-2.5 ${l.covered ? "" : "bg-red-50"}`} data-testid={`guard-item-${l.item_id}`}>
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-semibold truncate ${l.covered ? "text-slate-800" : "text-red-700"}`}>{l.name} <span className="font-mono-data text-slate-400">{l.sku}</span></p>
                  <p className="text-[10px] font-mono-data text-slate-400">
                    need {fmtQty(l.qty, l.unit)} {l.unit} · in stock <span className={l.covered ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>{l.onHand.toLocaleString("en-IN")}</span>
                    {!l.covered && <span className="text-red-600 font-semibold"> · short {l.short.toLocaleString("en-IN")}</span>}
                  </p>
                </div>
                {l.covered ? (
                  <span className="text-[10px] font-semibold text-emerald-600 shrink-0" data-testid={`guard-status-${l.item_id}`}>Ready ✓</span>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-semibold text-red-600" data-testid={`guard-status-${l.item_id}`}>Short</span>
                    {linked.length > 0 && onJumpToGantt && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px] border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={() => { onJumpToGantt(linked[0]); onClose(); }} data-testid={`guard-jump-${l.item_id}`}>
                        <GanttChartSquare className="h-3 w-3 mr-1" /> Jump to Production
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReservationGuardDialog({ view, confirming, onClose, onProceed }) {
  if (!view) return null;
  const { so, detail } = view;
  const conflicts = detail.reservation_conflicts || [];
  const n = (v) => Number(v || 0).toLocaleString("en-IN");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reservation-guard-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-amber-700"><Lock className="h-4 w-4" /> Reserved stock warning — {so.so_number}</DialogTitle>
          <DialogDescription className="text-xs">
            Confirming this order reserves finished goods already committed to other confirmed sales orders. Reserve anyway (this may oversell on-hand stock), or cancel and produce more first.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-amber-200 divide-y divide-amber-100 overflow-hidden mt-1 bg-amber-50/40">
          {conflicts.map((c, i) => (
            <div key={`${c.sku}-${i}`} className="flex items-center gap-2 px-3 py-2.5" data-testid={`reservation-conflict-${c.sku}`}>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-800 truncate">{c.name} <span className="font-mono-data text-slate-400">{c.sku}</span></p>
                <p className="text-[10px] font-mono-data text-slate-500">
                  need {n(c.requested)} {c.unit} · in stock {n(c.in_stock)} · already reserved <span className="text-amber-700 font-semibold">{n(c.reserved_elsewhere)}</span> · free to reserve <span className="text-emerald-600 font-semibold">{n(c.available)}</span>
                </p>
              </div>
              <span className="text-[10px] font-semibold text-red-600 shrink-0 whitespace-nowrap" data-testid={`reservation-over-${c.sku}`}>over by {n(c.over)} {c.unit}</span>
            </div>
          ))}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="reservation-guard-cancel">Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700" disabled={confirming} onClick={() => onProceed(so)} data-testid="reservation-guard-proceed">
            <Lock className="h-3.5 w-3.5 mr-1" /> {confirming ? "Reserving…" : "Reserve anyway"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalesPanel({ salesOrders, orders = [], customers, bomItems, canManage, isAdmin, onCreate, onUpdate, onConfirm, onFulfill, onCancel, onCreatePO, onRaiseAllPOs, onCreateWorkOrder, onImported, onJumpToGantt }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shortMap, setShortMap] = useState({});
  const [raiseTarget, setRaiseTarget] = useState(null); // { so, material }
  const [woTarget, setWoTarget] = useState(null); // { so, material } — Create Work Order
  const [chainView, setChainView] = useState(null); // { wo, materials } — chain-reaction POs
  const [refetchTick, setRefetchTick] = useState(0);
  const [guardView, setGuardView] = useState(null); // { so, guard }
  const [confirmGuard, setConfirmGuard] = useState(null); // { so, detail } — reservation lock warning
  const [confirming, setConfirming] = useState(false);
  const tn = useTn();
  const [aiOpen, setAiOpen] = useState(false);
  const handleConfirm = async (so, force = false) => {
    setConfirming(true);
    try {
      const r = await onConfirm(so, force);
      if (r?.conflict) { setConfirmGuard({ so, detail: r.conflict }); return; }
      setConfirmGuard(null);
    } finally { setConfirming(false); }
  };
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  // "Not yet fulfilled" (excluding cancelled) with a delivery date before today → overdue.
  const isOverdue = (so) =>
    !["Fulfilled", "Cancelled"].includes(so.status) && so.required_delivery_date && new Date(so.required_delivery_date) < startOfToday;
  const soRank = (so) => (isOverdue(so) ? -1 : (SO_ORDER[so.status] ?? 9)); // overdue floats to the very top
  const list = [...(salesOrders || [])].sort((a, b) => soRank(a) - soRank(b));
  const { pageItems, pagerProps } = usePager(list);
  const activeCustomers = (customers || []).filter((c) => c.active !== false);
  const componentIds = new Set();
  (bomItems || []).forEach((b) => (b.components || []).forEach((c) => componentIds.add(c.item_id)));
  const finishedGoods = (bomItems || []).filter((b) => b.item_type === "manufactured" && b.status !== "archived" && (b.approval_status || "Active") === "Active" && !componentIds.has(b.id));
  const stockById = Object.fromEntries((bomItems || []).map((b) => [b.id, b]));

  const openIds = list.filter((s) => ["Pending", "Confirmed"].includes(s.status)).map((s) => s.id);
  const openKey = openIds.join(",");
  useEffect(() => {
    let active = true;
    if (!openIds.length) { setShortMap({}); return; }
    Promise.all(
      openIds.map((id) => api.get(`/sales-orders/${id}/shortages`).then((d) => [id, d]).catch(() => [id, null]))
    ).then((pairs) => { if (active) setShortMap(Object.fromEntries(pairs)); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey, refetchTick]);

  const raisePO = async (payload) => {
    await onCreatePO(payload);
    setRefetchTick((t) => t + 1);
  };

  const raiseAllPOs = async (so) => {
    await onRaiseAllPOs?.(so);
    setRefetchTick((t) => t + 1);
  };

  const createWO = async (payload) => {
    const res = await onCreateWorkOrder?.(woTarget.so, payload);
    setWoTarget(null);
    setRefetchTick((t) => t + 1);
    if (res) setChainView({ wo: res.work_order, materials: res.raw_materials || [] });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="sales-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <ShoppingBag className="h-4 w-4 text-blue-600" />
        <h3 className="font-display font-semibold text-slate-800">Sales Orders</h3>
        <span className="text-xs text-slate-400" data-testid="sales-count">
          {list.filter((s) => !["Fulfilled", "Cancelled"].includes(s.status)).length} open · {list.length} total
        </span>
        {canManage && (
          <div className="ml-auto flex items-center gap-1.5">
            <ImportExportButtons entity="sales_orders" label="sales orders" allowUpsert={false} onImported={onImported} />
            {canManage && (
              <Button size="sm" variant="outline" className="h-7 text-[11px] border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => setAiOpen(true)} data-testid="ai-po-import-btn">
                <Sparkles className="h-3 w-3 mr-1" /> AI PO Import
              </Button>
            )}
            <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" onClick={() => setDialogOpen(true)} data-testid="add-sales-order-btn">
              <Plus className="h-3 w-3 mr-1" /> New Sales Order
            </Button>
          </div>
        )}
      </div>
      <div className={DT.wrap}>
        <table className={`${DT.table} min-w-[900px]`}>
          <thead className={DT.thead}>
            <tr className={DT.headerRow}>
              <th className="text-left font-semibold px-3 py-2">SO / Customer</th>
              <th className="text-left font-semibold px-3 py-2">Finished Goods</th>
              <th className="text-left font-semibold px-3 py-2">Cust. PO</th>
              <th className="text-left font-semibold px-3 py-2">Delivery</th>
              {SHOW_COSTING && <th className="text-right font-semibold px-3 py-2">Amount</th>}
              <th className="text-center font-semibold px-3 py-2">Status</th>
              <th className="text-right font-semibold px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 && <tr><td colSpan={SHOW_COSTING ? 7 : 6} className="px-3 py-8 text-center text-slate-400" data-testid="sales-empty">No sales orders yet.</td></tr>}
            {pageItems.map((so) => {
              const sh = shortMap[so.id];
              const guard = guardFor(so, stockById);
              const showGuard = ["Pending", "Confirmed"].includes(so.status);
              const GuardIcon = GUARD[guard.level].icon;
              return (
              <Fragment key={so.id}>
              <tr className={`border-b border-slate-100 transition-colors duration-150 even:bg-slate-50/60 hover:bg-slate-100/80 ${["Fulfilled", "Cancelled"].includes(so.status) ? "opacity-60" : ""}`} data-testid={`sales-row-${so.id}`}>
                <td className="px-3 py-2">
                  <p className="font-semibold text-slate-800 font-mono-data">{so.so_number}</p>
                  <p className="text-[11px] text-slate-500">{tn(so.customer_name)}</p>
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {(so.items || []).map((it, i) => (
                    <p key={`${it.item_id}-${i}`} className="text-[11px]">{tn(it.name)} <span className="font-mono-data text-slate-400">× {fmtQty(it.quantity, it.unit)} {it.unit}</span></p>
                  ))}
                  {(() => {
                    const linked = (orders || []).filter((o) => o.sales_order_id === so.id);
                    if (linked.length === 0) return null;
                    return (
                      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`sales-linked-${so.id}`}>
                        {linked.map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => onJumpToGantt?.(o)}
                            title={`View ${o.order_code} on the Gantt`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                            data-testid={`sales-gantt-link-${o.id}`}
                          >
                            <GanttChartSquare className="h-3 w-3" /> {o.order_code || "ORDER"} · {o.status}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {so.customer_po_number || "—"}
                  {so.po_date && <p className="text-[10px] text-slate-400">{so.po_date}</p>}
                </td>
                <td className="px-3 py-2 text-slate-600">{so.required_delivery_date || "—"}</td>
                {SHOW_COSTING && <td className="px-3 py-2 text-right font-mono-data text-slate-700" data-testid={`sales-amount-${so.id}`}>{so.total_amount > 0 ? formatINR(so.total_amount) : "—"}</td>}
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${isOverdue(so) ? "bg-red-100 text-red-700" : (STATUS_STYLE[so.status] || "bg-slate-100 text-slate-600")}`} data-testid={`sales-status-${so.id}`}>{isOverdue(so) ? "Confirmed and Overdue" : so.status}</span>
                  {showGuard && (
                    <button
                      type="button"
                      onClick={() => setGuardView({ so, guard })}
                      title="View stock readiness for this order"
                      className={`mt-1 mx-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${GUARD[guard.level].cls}`}
                      data-testid={`sales-guard-${so.id}`}
                      data-guard-level={guard.level}
                    >
                      <GuardIcon className="h-3 w-3" /> {GUARD[guard.level].label}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  {["Confirmed", "Fulfilled"].includes(so.status) && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => window.open(`${API}/sales-orders/${so.id}/confirmation.pdf`, "_blank")} data-testid={`sales-pdf-${so.id}`}>
                      <FileText className="h-3 w-3 mr-1" /> PDF
                    </Button>
                  )}
                  {so.status === "Fulfilled" && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => window.open(`${API}/sales-orders/${so.id}/delivery-note.pdf`, "_blank")} data-testid={`sales-delivery-note-${so.id}`}>
                      <Truck className="h-3 w-3 mr-1" /> Delivery Note
                    </Button>
                  )}
                  {isAdmin && !["Fulfilled", "Cancelled"].includes(so.status) && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setEditing(so)} data-testid={`sales-edit-${so.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  )}
                  {canManage && so.status === "Pending" && (
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleConfirm(so)} data-testid={`sales-confirm-${so.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Confirm
                    </Button>
                  )}
                  {canManage && so.status === "Confirmed" && (
                    <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed" disabled={guard.level !== "green"} title={guard.level !== "green" ? "Blocked: finished-goods stock isn't ready. Wait for production to complete." : "Ship finished goods and close this order"} onClick={() => onFulfill(so)} data-testid={`sales-fulfill-${so.id}`}>
                      <PackageCheck className="h-3 w-3 mr-1" /> Fulfill
                    </Button>
                  )}
                  {canManage && ["Pending", "Confirmed"].includes(so.status) && (
                    <Button size="sm" variant="ghost" className="h-7 text-[11px] text-red-500" onClick={() => onCancel(so)} data-testid={`sales-cancel-${so.id}`}>
                      <XCircle className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  )}
                  {!canManage && <span className="text-[11px] text-slate-400">Read only</span>}
                </td>
              </tr>
              {sh?.has_shortage && (() => {
                const buyMats = sh.materials.filter((m) => m.kind !== "make");
                const makeMats = sh.materials.filter((m) => m.kind === "make");
                return (
                <tr data-testid={`sales-shortage-row-${so.id}`}>
                  <td colSpan={SHOW_COSTING ? 7 : 6} className="px-3 pb-3 pt-0">
                    <div className="rounded-lg border border-red-200 bg-red-50/70 p-2.5">
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                        <PackageX className="h-3.5 w-3.5 text-red-600 shrink-0" />
                        <span className="text-[11px] font-semibold text-red-700">Shortage to produce {so.so_number}</span>
                        <span className="text-[10px] text-red-500">· {makeMats.length > 0 ? `${makeMats.length} to make` : ""}{makeMats.length > 0 && buyMats.length > 0 ? " · " : ""}{buyMats.length > 0 ? `${buyMats.length} to buy` : ""}</span>
                        {canManage && buyMats.length > 0 && (
                          <Button size="sm" className="ml-auto h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => raiseAllPOs(so)} data-testid={`sales-raise-all-po-${so.id}`}>
                            <ShoppingCart className="h-3 w-3 mr-1" /> Raise all POs
                          </Button>
                        )}
                      </div>
                      <div className="rounded-md border border-red-100 bg-white divide-y divide-red-50 overflow-hidden">
                        {sh.materials.map((m) => {
                          const isMake = m.kind === "make";
                          return (
                          <div key={m.item_id} className="flex items-center gap-2 px-2.5 py-1.5" data-testid={`sales-shortage-mat-${so.id}-${m.sku}`}>
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-semibold text-slate-800 truncate flex items-center gap-1.5">
                                {m.name} <span className="font-mono-data text-slate-400">{m.sku}</span>
                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${isMake ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`sales-shortage-kind-${so.id}-${m.sku}`}>
                                  {isMake ? "Make in-house" : "Buy"}
                                </span>
                              </p>
                              <p className="text-[10px] text-slate-400 font-mono-data">
                                need {fmtQty(m.required, m.unit)} · in stock {fmtQty(m.in_stock, m.unit)} · <span className="text-red-600 font-semibold">short {Number(m.shortage).toLocaleString("en-IN")} {m.unit}</span>
                                {m.supplier ? ` · ${m.supplier}` : ""}
                              </p>
                            </div>
                            {canManage && (isMake ? (
                              <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700 shrink-0" onClick={() => setWoTarget({ so, material: m })} data-testid={`sales-create-wo-${so.id}-${m.sku}`}>
                                <Wrench className="h-3 w-3 mr-1" /> Create Work Order
                              </Button>
                            ) : (
                              <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={() => setRaiseTarget({ so, material: m })} data-testid={`sales-raise-po-${so.id}-${m.sku}`}>
                                <ShoppingCart className="h-3 w-3 mr-1" /> Raise PO
                              </Button>
                            ))}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </td>
                </tr>
                );
              })()}
              </Fragment>
              );
            })}
          </tbody>
          {SHOW_COSTING && list.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td colSpan={4} className="px-3 py-2 text-right text-[11px] font-semibold uppercase text-slate-500">Total</td>
                <td className="px-3 py-2 text-right font-mono-data font-bold text-slate-900" data-testid="sales-total-amount">{formatINR(list.reduce((s, so) => s + (so.status === "Cancelled" ? 0 : (so.total_amount || 0)), 0))}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
        <ListPagination {...pagerProps} testid="sales-pagination" />
      </div>
      {dialogOpen && (
        <SalesOrderDialog
          customers={activeCustomers}
          finishedGoods={finishedGoods}
          onClose={() => setDialogOpen(false)}
          onSave={onCreate}
        />
      )}
      {editing && (
        <SalesOrderDialog
          customers={activeCustomers}
          finishedGoods={finishedGoods}
          existing={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => onUpdate(editing, payload)}
        />
      )}
      {raiseTarget && (
        <RaisePODialog
          material={raiseTarget.material}
          onClose={() => setRaiseTarget(null)}
          onSubmit={raisePO}
        />
      )}
      {woTarget && (
        <CreateWorkOrderFromSODialog
          material={woTarget.material}
          so={woTarget.so}
          onClose={() => setWoTarget(null)}
          onSubmit={createWO}
        />
      )}
      <ChainPODialog view={chainView} onClose={() => setChainView(null)} onRaisePO={raisePO} />
      <GuardDrawer view={guardView} orders={orders} onClose={() => setGuardView(null)} onJumpToGantt={onJumpToGantt} />
      <ReservationGuardDialog view={confirmGuard} confirming={confirming} onClose={() => setConfirmGuard(null)} onProceed={(so) => handleConfirm(so, true)} />
      {aiOpen && (
        <AiPoImportDialog onClose={() => setAiOpen(false)} customers={customers} finishedGoods={finishedGoods} onCreate={onCreate} />
      )}
    </div>
  );
}
